// src/pages/admin/AdminRoutes.jsx
import React, { useState } from "react";
import { Routes, Route, NavLink, Outlet, Navigate } from "react-router-dom";
import PropertiesAdmin from "./properties/PropertiesAdmin";
import PropertyForm from "./properties/PropertyForm";
import PropertyDetailAdmin from "./properties/PropertyDetailAdmin";
import RoomTypesList from "./room-types/RoomTypesAdmin";
import RoomTypeForm from "./room-types/RoomTypeForm";
import UnitsList from "./units/UnitsAdmin";
import UnitForm from "./units/UnitForm";
import { Home, Building2, Boxes, Menu, X, Grid3x3, Users, Settings, UserX, BarChart3, Receipt } from "lucide-react";
import ProtectedRoute from "../components/ProtectedRoute";
import RoleGate from "../components/RoleGate";
import { useAuth } from "../context/AuthContext";
import SettingsAdmin from "./SettingsAdmin";
import ApplicantsAdmin from "./ApplicantsAdmin";
import TenantsAdmin from "./TenantsAdmin";
import InactivesAdmin from "./InactivesAdmin";
import ApplicantProfile from "./profiles/ApplicantProfile";
import TenantProfile from "./profiles/TenantProfile";
import AnalyticsDashboard from "./analytics/AnalyticsDashboard";
import UsersAdmin from "./users/UsersAdmin";
import BillingAdmin from "./BillingAdmin";

function AdminLayout() {
  const { logout, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navLinkBase =
    "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100";

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${navLinkBase} ${isActive ? "bg-gray-900 text-white hover:bg-gray-900" : "text-gray-700"}`
      }
      onClick={() => setSidebarOpen(false)}
    >
      {Icon ? <Icon className="w-4 h-4" /> : null}
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar (mobile + desktop) */}
      <header className="sticky top-0 bg-white border-b z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border"
              onClick={() => setSidebarOpen((s) => !s)}
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              <span className="font-bold">Admin</span>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <span className="text-sm text-gray-600">{user?.name}</span>
            <button
              onClick={() => {
                const val = window.prompt("Enter ADMIN token (x-admin-token)", localStorage.getItem("adminToken") || "");
                if (val != null) {
                  if (val) localStorage.setItem("adminToken", val);
                  else localStorage.removeItem("adminToken");
                }
              }}
              className="px-3 py-1.5 border rounded-md text-sm"
              title="Set admin token header for API"
            >
              Set Token
            </button>
            <button onClick={logout} className="px-3 py-1.5 border rounded-md text-sm">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed z-40 top-[57px] bottom-0 left-0 w-64 bg-white border-r transform transition-transform duration-200 ease-out \
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:top-[57px]`}
      >
        <div className="h-full flex flex-col">
          <div className="p-3 border-b">
            <NavItem to="/" icon={Home} label="Back to Site" />
          </div>

          <nav className="flex-1 p-3 space-y-1">
            <RoleGate allow={["admin"]}>
              <NavItem to="/admin/properties" icon={Building2} label="Properties" />
              <NavItem to="/admin/room-types" icon={Boxes} label="Room Types" />
            </RoleGate>
            <RoleGate allow={["admin","agent"]}>
              <NavItem to="/admin/units" icon={Grid3x3} label="Units" />
              <NavItem to="/admin/applicants" icon={Users} label="Applicants" />
              <NavItem to="/admin/tenants" icon={Users} label="Tenants" />
              <NavItem to="/admin/billing" icon={Receipt} label="Billing" />
              <NavItem to="/admin/inactives" icon={UserX} label="Inactives" />
            </RoleGate>
            <RoleGate allow={["admin"]}>
              <NavItem to="/admin/analytics" icon={BarChart3} label="Analytics" />
              <NavItem to="/admin/settings" icon={Settings} label="Settings" />
              <NavItem to="/admin/users" icon={Users} label="Users" />
            </RoleGate>
          </nav>

          <div className="p-3 border-t md:hidden">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">{user?.name}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const val = window.prompt("Enter ADMIN token (x-admin-token)", localStorage.getItem("adminToken") || "");
                    if (val != null) {
                      if (val) localStorage.setItem("adminToken", val);
                      else localStorage.removeItem("adminToken");
                    }
                  }}
                  className="px-3 py-1.5 border rounded-md text-sm"
                >
                  Set Token
                </button>
                <button onClick={logout} className="px-3 py-1.5 border rounded-md text-sm">
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Content area */}
      <main className="md:ml-64 p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}

export default function AdminRoutes() {
  function DefaultLanding() {
    // Choose default route by role
    try {
      const raw = localStorage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      const role = (user?.role || '').toLowerCase();
      return <Navigate to={role === 'agent' ? 'units' : 'properties'} replace />;
    } catch {
      return <Navigate to="properties" replace />;
    }
  }
  return (
    <Routes>
      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        {/* Default /admin: agents -> applicants, admins -> properties */}
        <Route index element={<DefaultLanding />} />
        <Route path="properties" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><PropertiesAdmin /></RoleGate>} />
        <Route path="properties/new" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><PropertyForm mode="create" /></RoleGate>} />
        <Route path="properties/:slug/edit" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><PropertyForm mode="edit" /></RoleGate>} />
        <Route path="properties/:slug" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><PropertyDetailAdmin /></RoleGate>} />
        {/* Room Types */}
        <Route path="room-types" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><RoomTypesList /></RoleGate>} />
        <Route path="room-types/new" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><RoomTypeForm mode="create" /></RoleGate>} />
        <Route path="room-types/:id/edit" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><RoomTypeForm mode="edit" /></RoleGate>} />

        {/* Units */}
        <Route path="units" element={<RoleGate allow={["admin","agent"]} fallback={<Navigate to="/admin" replace/>}><UnitsList /></RoleGate>} />
        <Route path="units/new" element={<UnitForm mode="create" />} />
        <Route path="units/:id/edit" element={<UnitForm mode="edit" />} />

        {/* Applicants / Tenants / Settings */}
        <Route path="applicants" element={<RoleGate allow={["admin","agent"]} fallback={<Navigate to="/admin" replace/>}><ApplicantsAdmin /></RoleGate>} />
        <Route path="applicants/:id" element={<ApplicantProfile />} />
        <Route path="tenants" element={<RoleGate allow={["admin","agent"]} fallback={<Navigate to="/admin" replace/>}><TenantsAdmin /></RoleGate>} />
        <Route path="tenants/:id" element={<TenantProfile />} />
        <Route path="billing" element={<RoleGate allow={["admin","agent"]} fallback={<Navigate to="/admin" replace/>}><BillingAdmin /></RoleGate>} />
        <Route path="inactives" element={<RoleGate allow={["admin","agent"]} fallback={<Navigate to="/admin" replace/>}><InactivesAdmin /></RoleGate>} />
        <Route path="inactives/:id" element={<TenantProfile />} />
        <Route path="analytics" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><AnalyticsDashboard /></RoleGate>} />
        <Route path="settings" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><SettingsAdmin /></RoleGate>} />
        <Route path="users" element={<RoleGate allow={["admin"]} fallback={<Navigate to="/admin" replace/>}><UsersAdmin /></RoleGate>} />
      </Route>
    </Routes>
  );
}
